import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ChevronLeft,
    ShieldCheck,
    Cookie,
    UserCheck,
    Mail,
    Server,
    BadgeInfo,
    ExternalLink,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { trackPageview } from "@/lib/analytics";

/**
 * Public /polityka-prywatnosci page - GDPR-compliant privacy policy
 * covering:
 *   - Data controller / contact info
 *   - Legal basis (GDPR art. 6),
 *   - Purposes and retention,
 *   - User rights (art. 15-22),
 *   - Cookies (technical + analytics + advertising for Google AdSense),
 *   - Third-party recipients (Google AdSense partners, analytics),
 *   - Personalised ads disclosure required by AdSense EU user consent policy.
 *
 * Written in accessible Polish so a non-lawyer gamer can understand it,
 * while still meeting the AdSense EU consent-message requirements.
 */
export default function PrivacyPolicy() {
    const [modInfo, setModInfo] = useState(null);

    useEffect(() => {
        trackPageview("/polityka-prywatnosci");
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/mod-info");
                setModInfo(data);
            } catch {
                /* keep defaults */
            }
        })();
    }, []);

    // Try to guess a contact email from mod-info; fall back to a placeholder.
    const contactEmail =
        modInfo?.contact_email || modInfo?.seo_contact_email || "";

    return (
        <div className="App" data-testid="privacy-policy-page">
            <SEO
                modInfo={modInfo}
                title="Polityka prywatności"
                description="Polityka prywatności strony Project Ruda Śląska - informacje o RODO, plikach cookies, Google AdSense i reklamach spersonalizowanych."
                pathname="/polityka-prywatnosci"
            />
            <Header />

            <main
                className="pb-24"
                style={{ paddingTop: "calc(6rem + var(--announcement-h, 0px))" }}
            >
                <section className="max-w-4xl mx-auto px-6">
                    <Link
                        to="/"
                        data-testid="privacy-back-home"
                        className="inline-flex items-center gap-2 text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] text-xs uppercase tracking-[0.25em] mb-6"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Powrót do strony
                    </Link>

                    <div className="flex items-start gap-4 mb-8">
                        <div className="p-3 rounded-xl bg-[color:var(--primary)]/15 border border-[color:var(--primary)]/40">
                            <ShieldCheck className="w-7 h-7 text-[color:var(--primary)]" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.35em] text-[color:var(--text-secondary)] mb-1">
                                Dokument · RODO / Cookies
                            </div>
                            <h1
                                data-testid="privacy-title"
                                className="text-3xl md:text-5xl font-black text-[color:var(--text-primary)] leading-tight"
                            >
                                Polityka prywatności
                            </h1>
                            <p className="text-[color:var(--text-secondary)] mt-3 max-w-2xl text-sm md:text-base">
                                Ta strona wyjaśnia w prosty sposób, jakie
                                dane są zbierane w serwisie{" "}
                                <b>Project Ruda Śląska</b>, w jaki sposób
                                są wykorzystywane, jak długo są
                                przechowywane oraz jakie prawa
                                przysługują użytkownikowi zgodnie z
                                <abbr title="Rozporządzenie Parlamentu Europejskiego i Rady (UE) 2016/679 z 27 kwietnia 2016 r.">
                                    {" RODO"}
                                </abbr>.
                            </p>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] mt-4">
                                Ostatnia aktualizacja:{" "}
                                {new Date().toLocaleDateString("pl-PL", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                })}
                            </p>
                        </div>
                    </div>

                    {/* Section 1: Administrator danych */}
                    <Section
                        n="1"
                        icon={UserCheck}
                        title="Administrator danych osobowych"
                        testid="privacy-section-1"
                    >
                        <p>
                            Administratorem danych osobowych w rozumieniu
                            RODO jest autor projektu{" "}
                            <b>Project Ruda Śląska</b> - fanowskiej
                            modyfikacji mapy 1:1 do gry{" "}
                            <i>Euro Truck Simulator 2</i>. Projekt jest
                            prowadzony hobbystycznie przez osobę
                            fizyczną, nie ma charakteru komercyjnego.
                        </p>
                        <p>
                            W sprawach dotyczących ochrony danych
                            osobowych można kontaktować się:
                        </p>
                        <ul className="list-disc pl-6 space-y-1">
                            {contactEmail && (
                                <li>
                                    e-mail:{" "}
                                    <a
                                        className="underline hover:text-[color:var(--primary)]"
                                        href={`mailto:${contactEmail}`}
                                    >
                                        {contactEmail}
                                    </a>
                                </li>
                            )}
                            <li>
                                przez kanały opisane w sekcji{" "}
                                <Link
                                    className="underline hover:text-[color:var(--primary)]"
                                    to="/#kontakt"
                                >
                                    Społeczność
                                </Link>{" "}
                                na stronie głównej.
                            </li>
                        </ul>
                    </Section>

                    {/* Section 2: Zakres zbieranych danych */}
                    <Section
                        n="2"
                        icon={Server}
                        title="Jakie dane zbieramy"
                        testid="privacy-section-2"
                    >
                        <p>
                            Zbieramy wyłącznie te dane, które są nam
                            niezbędne do prawidłowego działania serwisu i
                            komunikacji z użytkownikami. W
                            szczególności:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>
                                <b>Dane techniczne</b>: adres IP,
                                identyfikator przeglądarki (user-agent),
                                typ urządzenia i systemu operacyjnego -
                                zbierane automatycznie podczas
                                przeglądania strony w celach
                                statystycznych i bezpieczeństwa.
                            </li>
                            <li>
                                <b>Dane dobrowolnie podane w formularzach</b>:
                                np. w formularzu „Zgłoś błąd” (opcjonalny
                                adres e-mail do kontaktu zwrotnego,
                                opis błędu, załączniki graficzne). Dane
                                są używane wyłącznie do obsługi zgłoszenia.
                            </li>
                            <li>
                                <b>Dane z plików cookies</b>: patrz
                                sekcja 6 poniżej.
                            </li>
                        </ul>
                        <p className="text-[color:var(--text-muted)] text-sm">
                            Nie zbieramy tzw. „danych szczególnych
                            kategorii” (np. dotyczących zdrowia,
                            pochodzenia etnicznego, poglądów
                            politycznych).
                        </p>
                    </Section>

                    {/* Section 3: Cele i podstawy prawne */}
                    <Section
                        n="3"
                        icon={BadgeInfo}
                        title="Cele przetwarzania i podstawy prawne"
                        testid="privacy-section-3"
                    >
                        <ul className="list-disc pl-6 space-y-2">
                            <li>
                                <b>Świadczenie usług serwisu</b> -
                                udostępnianie treści strony (art. 6 ust.
                                1 lit. f RODO - uzasadniony interes
                                administratora).
                            </li>
                            <li>
                                <b>Obsługa zgłoszeń użytkowników</b> -
                                odpowiadanie na wiadomości i zgłoszenia
                                błędów (art. 6 ust. 1 lit. a RODO -
                                zgoda oraz lit. f - uzasadniony interes
                                w postaci rozwoju projektu).
                            </li>
                            <li>
                                <b>Statystyki i analityka</b> -
                                zrozumienie sposobu korzystania ze strony
                                w celu ulepszania jej funkcji (art. 6
                                ust. 1 lit. a RODO - zgoda wyrażona przez
                                zaakceptowanie plików cookies).
                            </li>
                            <li>
                                <b>Wyświetlanie reklam</b> - w tym
                                reklam spersonalizowanych za
                                pośrednictwem Google AdSense (art. 6
                                ust. 1 lit. a RODO - zgoda).
                            </li>
                        </ul>
                    </Section>

                    {/* Section 4: Okres przechowywania */}
                    <Section
                        n="4"
                        icon={Server}
                        title="Jak długo przechowujemy dane"
                        testid="privacy-section-4"
                    >
                        <ul className="list-disc pl-6 space-y-2">
                            <li>
                                <b>Zgłoszenia błędów</b>: do 24 miesięcy
                                od daty rozwiązania zgłoszenia (potem
                                automatycznie usuwane albo
                                anonimizowane).
                            </li>
                            <li>
                                <b>Logi serwera i statystyki
                                analityczne</b>: do 12 miesięcy.
                            </li>
                            <li>
                                <b>Pliki cookies</b>: zgodnie z czasem
                                życia poszczególnych ciasteczek -
                                większość wygasa po 12 miesiącach lub w
                                momencie zamknięcia przeglądarki (patrz
                                sekcja 6).
                            </li>
                        </ul>
                    </Section>

                    {/* Section 5: Prawa użytkownika */}
                    <Section
                        n="5"
                        icon={UserCheck}
                        title="Prawa użytkownika (art. 15–22 RODO)"
                        testid="privacy-section-5"
                    >
                        <p>
                            W związku z przetwarzaniem danych osobowych
                            użytkownikowi przysługują następujące
                            prawa:
                        </p>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>
                                <b>Dostępu do danych</b> i uzyskania ich
                                kopii (art. 15).
                            </li>
                            <li>
                                <b>Sprostowania</b> danych nieprawidłowych
                                lub niekompletnych (art. 16).
                            </li>
                            <li>
                                <b>Usunięcia danych</b> - „prawo do bycia
                                zapomnianym” (art. 17).
                            </li>
                            <li>
                                <b>Ograniczenia przetwarzania</b> (art. 18).
                            </li>
                            <li>
                                <b>Przenoszenia danych</b> do innego
                                administratora (art. 20).
                            </li>
                            <li>
                                <b>Sprzeciwu</b> wobec przetwarzania
                                danych opartego na uzasadnionym
                                interesie administratora (art. 21).
                            </li>
                            <li>
                                <b>Wycofania zgody</b> w dowolnym momencie
                                - bez wpływu na zgodność z prawem
                                przetwarzania sprzed cofnięcia zgody.
                            </li>
                            <li>
                                Wniesienia skargi do{" "}
                                <b>Prezesa Urzędu Ochrony Danych
                                Osobowych</b>{" "}
                                (ul. Stawki 2, 00-193 Warszawa) -
                                jeżeli uważasz, że przetwarzanie
                                Twoich danych narusza przepisy prawa.
                            </li>
                        </ul>
                        <p className="text-[color:var(--text-muted)] text-sm">
                            Aby skorzystać z powyższych praw, wystarczy
                            skontaktować się z administratorem
                            (patrz sekcja 1).
                        </p>
                    </Section>

                    {/* Section 6: Cookies */}
                    <Section
                        n="6"
                        icon={Cookie}
                        title="Pliki cookies i podobne technologie"
                        testid="privacy-section-6"
                    >
                        <p>
                            Serwis wykorzystuje pliki cookies (tzw.
                            „ciasteczka”) oraz podobne technologie
                            (localStorage, sessionStorage), które
                            zapisywane są w przeglądarce użytkownika.
                            Dzielimy je na trzy kategorie:
                        </p>
                        <div className="space-y-4 mt-2">
                            <CookieCategory
                                name="Cookies niezbędne"
                                required
                                description={
                                    <>
                                        Zapewniają podstawowe działanie
                                        strony - pamiętanie zaakceptowanej
                                        zgody na cookies, utrzymanie
                                        sesji w panelu administracyjnym,
                                        zabezpieczenia (m.in.
                                        rate-limiting). Bez nich strona
                                        nie może działać poprawnie.
                                    </>
                                }
                            />
                            <CookieCategory
                                name="Cookies analityczne"
                                description={
                                    <>
                                        Pomagają zrozumieć, jak
                                        użytkownicy korzystają ze strony
                                        (najczęściej odwiedzane sekcje,
                                        źródła ruchu, typy urządzeń).
                                        Dzięki temu ulepszamy zawartość
                                        i wydajność serwisu. Dane
                                        analityczne są przetwarzane w
                                        formie zagregowanej.
                                    </>
                                }
                            />
                            <CookieCategory
                                name="Cookies reklamowe (Google AdSense)"
                                description={
                                    <>
                                        Wykorzystywane przez{" "}
                                        <b>Google AdSense</b> i jego
                                        partnerów do wyświetlania{" "}
                                        <b>reklam spersonalizowanych</b>{" "}
                                        na podstawie zainteresowań
                                        użytkownika oraz mierzenia
                                        skuteczności reklam. Google może
                                        przekazywać te dane swoim
                                        partnerom reklamowym
                                        (uczestnikom TCF v2).
                                    </>
                                }
                            />
                        </div>
                        <p className="text-sm text-[color:var(--text-secondary)] mt-4">
                            Zgoda na cookies analityczne i reklamowe jest
                            <b> dobrowolna</b> i można ją w każdej chwili{" "}
                            <b>wycofać lub zmienić</b> - użyj do tego
                            baneru „Ustawienia cookies”, który pojawia
                            się przy pierwszym wejściu, lub ustawień
                            plików cookies w swojej przeglądarce.
                        </p>
                    </Section>

                    {/* Section 7: Google AdSense - personalised ads */}
                    <Section
                        n="7"
                        icon={BadgeInfo}
                        title="Reklamy i Google AdSense"
                        testid="privacy-section-7"
                        highlight
                    >
                        <p>
                            Strona <b>może wyświetlać reklamy
                            spersonalizowane</b> dostarczane przez
                            zewnętrznego dostawcę reklam{" "}
                            <b>Google AdSense</b> (Google Ireland Ltd.).
                            Reklamy te są dobierane m.in. na podstawie:
                        </p>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>
                                wcześniej odwiedzanych witryn i
                                zainteresowań użytkownika,
                            </li>
                            <li>
                                lokalizacji urządzenia (na poziomie
                                miasta / regionu),
                            </li>
                            <li>
                                identyfikatorów reklamowych i plików
                                cookies zapisanych w przeglądarce.
                            </li>
                        </ul>
                        <p>
                            Google, jako <b>zewnętrzny dostawca</b>,
                            używa plików cookies do wyświetlania reklam
                            w oparciu o wcześniejsze wizyty użytkownika
                            w naszej witrynie i innych witrynach.
                            Użytkownik może w każdej chwili
                            zrezygnować z reklam spersonalizowanych,
                            odwiedzając{" "}
                            <a
                                className="underline hover:text-[color:var(--primary)] inline-flex items-center gap-1"
                                href="https://www.google.com/settings/ads"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Ustawienia reklam Google
                                <ExternalLink className="w-3 h-3" />
                            </a>{" "}
                            lub{" "}
                            <a
                                className="underline hover:text-[color:var(--primary)] inline-flex items-center gap-1"
                                href="https://www.youronlinechoices.eu/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                youronlinechoices.eu
                                <ExternalLink className="w-3 h-3" />
                            </a>.
                        </p>
                        <p>
                            Szczegóły przetwarzania danych przez Google
                            znajdują się w{" "}
                            <a
                                className="underline hover:text-[color:var(--primary)] inline-flex items-center gap-1"
                                href="https://policies.google.com/technologies/ads"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Polityce reklamowej Google
                                <ExternalLink className="w-3 h-3" />
                            </a>{" "}
                            oraz{" "}
                            <a
                                className="underline hover:text-[color:var(--primary)] inline-flex items-center gap-1"
                                href="https://policies.google.com/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Polityce prywatności Google
                                <ExternalLink className="w-3 h-3" />
                            </a>.
                        </p>
                    </Section>

                    {/* Section 8: Odbiorcy danych */}
                    <Section
                        n="8"
                        icon={Server}
                        title="Odbiorcy danych i transfer poza EOG"
                        testid="privacy-section-8"
                    >
                        <p>
                            Dane osobowe mogą być przekazywane
                            następującym kategoriom odbiorców:
                        </p>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>
                                dostawcom usług hostingowych i
                                infrastruktury (serwery, CDN);
                            </li>
                            <li>
                                dostawcy usług analityki webowej;
                            </li>
                            <li>
                                <b>Google Ireland Ltd.</b> (Google
                                AdSense) i jego partnerom reklamowym z
                                listy TCF v2.
                            </li>
                        </ul>
                        <p>
                            Część tych podmiotów (m.in. Google) może
                            przetwarzać dane <b>poza Europejskim
                            Obszarem Gospodarczym</b> (EOG). W takich
                            przypadkach transfer odbywa się na
                            podstawie standardowych klauzul umownych
                            zatwierdzonych przez Komisję Europejską
                            lub innych podstaw zgodnych z RODO.
                        </p>
                    </Section>

                    {/* Section 9: Zmiany polityki */}
                    <Section
                        n="9"
                        icon={Mail}
                        title="Zmiany w polityce prywatności"
                        testid="privacy-section-9"
                    >
                        <p>
                            Zastrzegamy sobie prawo do aktualizacji
                            niniejszej polityki. Data ostatniej
                            aktualizacji widoczna jest u góry strony.
                            Istotne zmiany będą komunikowane na stronie
                            głównej serwisu przez okres co najmniej 14
                            dni.
                        </p>
                        <p className="text-[color:var(--text-muted)] text-sm">
                            Ta polityka nie stanowi porady prawnej ani
                            wiążącej wykładni RODO - w sprawach spornych
                            zawsze zachęcamy do kontaktu z
                            administratorem lub konsultacji z
                            prawnikiem.
                        </p>
                    </Section>
                </section>
            </main>

            <Footer />
        </div>
    );
}

// ---- Local components -----------------------------------------------------

function Section({ n, icon: Icon, title, children, testid, highlight = false }) {
    return (
        <div
            data-testid={testid}
            className={`panel-yellow p-6 md:p-8 mb-6 ${
                highlight
                    ? "border border-[color:var(--primary)]/50"
                    : ""
            }`}
        >
            <div className="flex items-start gap-4">
                <div className="shrink-0 flex flex-col items-center gap-2">
                    <div className="w-11 h-11 rounded-lg bg-[color:var(--primary)]/15 border border-[color:var(--primary)]/40 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[color:var(--primary)]" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)] font-bold">
                        {n}
                    </span>
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="text-lg md:text-xl font-black text-[color:var(--text-primary)] mb-3">
                        {title}
                    </h2>
                    <div className="space-y-3 text-sm md:text-base text-[color:var(--text-secondary)] leading-relaxed">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}

function CookieCategory({ name, description, required = false }) {
    return (
        <div className="rounded-lg border border-white/10 bg-black/20 p-4">
            <div className="flex items-center gap-2 mb-1.5">
                <span className="text-sm font-black text-[color:var(--text-primary)]">
                    {name}
                </span>
                {required && (
                    <span className="text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded bg-[color:var(--primary)]/20 text-[color:var(--primary)] border border-[color:var(--primary)]/40">
                        wymagane
                    </span>
                )}
            </div>
            <div className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                {description}
            </div>
        </div>
    );
}
